/**
 * questHl7.ts
 *
 * TypeScript port of loadQuestLabPreset() from assets-ftp/js/lab-quest-handling.js.
 *
 * The stored fullOrderContent is an HL7 ORM^O01 message where every segment is
 * serialised as `NAME|f1|f2|...|fn|` (a trailing field separator after the last
 * field — see Net_HL7_Message::toString) and segments are joined with "\r".
 * Splitting the whole message on "|" therefore yields each segment name as a
 * clean token (after trim), exactly like the legacy JS, so we reuse the same
 * field offsets.
 *
 * Parity notes (mirroring the legacy function):
 *   - For a saved ORDER we hydrate facility, provider, patient demographics,
 *     PID notes, tests, insurance, guarantor and diagnoses.
 *   - For a PRESET only the tests (OBR) are hydrated; the legacy function gates
 *     every other segment behind `type == 'order'`.
 */

export interface ParsedQuestNote {
  text: string;
  type: 'I' | 'R';
}

export interface ParsedQuestTest {
  testCode: string;
  description: string;
  collectionDate: string;
  obrNotes: ParsedQuestNote[];
}

export interface ParsedQuestInsurance {
  billingType: string;
  insuranceId: string;
  insuranceName: string;
  policyNumber: string;
  groupNumber: string;
  coStreet: string;
  coCity: string;
  coState: string;
  coZip: string;
  relationship: string;
  guarantorFirstName: string;
  guarantorMiddleName: string;
  guarantorLastName: string;
  guarantorAddress: string;
  guarantorCity: string;
  guarantorState: string;
  guarantorZip: string;
  guarantorPhone: string;
  insuredFirstName: string;
  insuredMiddleName: string;
  insuredLastName: string;
  insuredAddress: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
}

export interface ParsedQuestDiagnosis {
  code: string;
  title: string;
}

export interface ParsedQuestOrder {
  facility?: string;
  provider?: string;
  patient?: {
    firstName: string;
    lastName: string;
    birthYear: string;
    birthMonth: string;
    birthDay: string;
    gender: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
  };
  pidNotes: ParsedQuestNote[];
  tests: ParsedQuestTest[];
  insurance: ParsedQuestInsurance[];
  diagnoses: ParsedQuestDiagnosis[];
}

function noteType(value: string | undefined): 'I' | 'R' {
  return value === 'R' ? 'R' : 'I';
}

export function parseQuestHl7Order(
  fullOrderContent: string,
  type: 'order' | 'preset',
  defaultCollectionDate: string,
): ParsedQuestOrder {
  const foc = String(fullOrderContent ?? '').split('|');
  const isOrder = type === 'order';

  const result: ParsedQuestOrder = {
    pidNotes: [],
    tests: [],
    insurance: [],
    diagnoses: [],
  };

  // Guarantor (GT1) is singular and shared across insurance entries; captured
  // in the first pass like the legacy code.
  let gt1FirstName = '';
  let gt1LastName = '';
  let gt1MiddleName = '';
  let gt1Address = '';
  let gt1City = '';
  let gt1State = '';
  let gt1Zip = '';
  let gt1Phone = '';

  // ── First pass: MSH (facility), ORC (provider), GT1 (guarantor) ──────────
  for (let ew = 0; ew < foc.length; ew++) {
    const seg = (foc[ew] ?? '').trim();
    if (isOrder && seg === 'MSH') {
      result.facility = foc[ew + 3];
    }
    if (isOrder && seg === 'ORC') {
      const providerArray = (foc[ew + 12] ?? '').split('^');
      result.provider = providerArray[0];
    }
    if (isOrder && seg === 'GT1') {
      const gt1AddressArray = (foc[ew + 5] ?? '').split('^');
      gt1Address = gt1AddressArray[0] ?? '';
      gt1City = gt1AddressArray[2] ?? '';
      gt1State = gt1AddressArray[3] ?? '';
      gt1Zip = gt1AddressArray[4] ?? '';
      const gt1Name = (foc[ew + 3] ?? '').split('^');
      gt1FirstName = gt1Name[1] ?? '';
      gt1LastName = gt1Name[0] ?? '';
      gt1MiddleName = typeof gt1Name[2] === 'undefined' ? '' : gt1Name[2];
      gt1Phone = foc[ew + 6] ?? '';
    }
  }

  // ── Second pass: PID, OBR (+ following OBR NTE), PID NTE, IN1, DG1 ─────────
  let lastSegment = '';
  let currentTestIndex = -1;

  for (let ew = 0; ew < foc.length; ew++) {
    const seg = (foc[ew] ?? '').trim();

    if (seg === 'PID' && isOrder) {
      lastSegment = 'PID';
      const answerArray = (foc[ew + 5] ?? '').split('^');
      const dateString = foc[ew + 7] ?? '';
      const addressArray = (foc[ew + 11] ?? '').split('^');
      result.patient = {
        lastName: answerArray[0] ?? '',
        firstName: answerArray[1] ?? '',
        birthYear: dateString.substr(0, 4),
        birthMonth: dateString.substr(4, 2),
        birthDay: dateString.substr(6, 2),
        gender: foc[ew + 8] ?? '',
        street: addressArray[0] ?? '',
        city: addressArray[2] ?? '',
        state: addressArray[3] ?? '',
        zip: addressArray[4] ?? '',
        phone: foc[ew + 13] ?? '',
      };
    } else if (seg === 'OBR') {
      const optionArray = (foc[ew + 4] ?? '').split('^');
      const testCode = optionArray[3] ?? '';
      const testTitle = optionArray[4] ?? '';
      let collectionDate = '';
      if (isOrder) {
        const raw = foc[ew + 7] ?? '';
        if (raw !== '') {
          collectionDate = raw.substr(0, 4) + '-' + raw.substr(4, 2) + '-' + raw.substr(6, 2);
        }
      } else {
        collectionDate = defaultCollectionDate;
      }
      result.tests.push({
        testCode,
        description: testTitle,
        collectionDate,
        obrNotes: [],
      });
      currentTestIndex = result.tests.length - 1;
      lastSegment = 'OBR';
    } else if (seg === 'NTE') {
      const nType = noteType(foc[ew + 2]);
      const nText = foc[ew + 3] ?? '';
      if (lastSegment === 'PID' && isOrder) {
        result.pidNotes.push({ text: nText, type: nType });
      } else if (lastSegment === 'OBR' && currentTestIndex >= 0) {
        result.tests[currentTestIndex].obrNotes.push({ text: nText, type: nType });
      }
    } else if (seg === 'IN1' && isOrder) {
      const in1Title = foc[ew + 4] ?? '';
      const in1Type = foc[ew + 47] ?? '';
      const coAddressArray = (foc[ew + 5] ?? '').split('^');
      const insuredAddressArray = (foc[ew + 19] ?? '').split('^');
      const insuredName = (foc[ew + 16] ?? '').split('^');
      result.insurance.push({
        billingType: in1Type,
        insuranceId: '',
        insuranceName: in1Title,
        policyNumber: foc[ew + 36] ?? '',
        groupNumber: foc[ew + 8] ?? '',
        coStreet: coAddressArray[0] ?? '',
        coCity: coAddressArray[2] ?? '',
        coState: coAddressArray[3] ?? '',
        coZip: coAddressArray[4] ?? '',
        relationship: foc[ew + 17] ?? '',
        guarantorFirstName: gt1FirstName,
        guarantorMiddleName: gt1MiddleName,
        guarantorLastName: gt1LastName,
        guarantorAddress: gt1Address,
        guarantorCity: gt1City,
        guarantorState: gt1State,
        guarantorZip: gt1Zip,
        guarantorPhone: gt1Phone,
        insuredFirstName: insuredName[1] ?? '',
        insuredMiddleName: typeof insuredName[2] === 'undefined' ? '' : insuredName[2],
        insuredLastName: insuredName[0] ?? '',
        insuredAddress: insuredAddressArray[0] ?? '',
        insuredCity: insuredAddressArray[2] ?? '',
        insuredState: insuredAddressArray[3] ?? '',
        insuredZip: insuredAddressArray[4] ?? '',
      });
    } else if (seg === 'DG1' && isOrder) {
      const dg1Code = foc[ew + 3] ?? '';
      const dg1Text = foc[ew + 4] ?? '';
      if (dg1Code !== '') {
        result.diagnoses.push({ code: dg1Code, title: dg1Text });
      }
    }
  }

  return result;
}
